import { Injectable } from "@angular/core";
import ZoomVideo from "@zoom/videosdk";
import { Logger } from "aws-amplify";
import { canvas } from "leaflet";

export const VIDEO_QUALITY_90P = 0;
export const VIDEO_QUALITY_180P = 1;
export const VIDEO_QUALITY_360P = 2;
export const VIDEO_QUALITY_720P = 3;

/*
 * Dimensions and offset need to be carefully set to render multiple streams on a
 * single canvas. There are several things to keep in mind:
 *      1) Maintaining 16:9 aspect ratio (or whatever most participants have)
 *      2) Setting suitable height/width based on the render canvas dimensions
 *      3) Setting the correct offset
 *
 * For this demo, we have a simple example with only two video streams:
 * |------------------------------------------------------------------|
 * |                                |                                 |
 * |                                |                                 |
 * |          Participant           |              Self               |
 * |                                |                                 |
 * |                                |                                 |
 * |                                |                                 |
 * |------------------------------------------------------------------|
 *
 * To achieve the above, the participant stream is rendered with no offset and a
 * width equal to `canvas_width / 2`. For height, while we would typically need
 * to handle aspect ratio and vertical centering ourselves, the V-SDK automatically
 * handles it -- so just pass the canvas height
 *
 * We do the exact same for the self stream, but now have an x-offset of
 * `cavas_width / 2`
 *
 * This simple example can be extended to larger numbers (3, 4, etc.) and
 * dynamically adjusted based on the active number of participants!
 */

@Injectable({
    providedIn: "root",
})
export class ZoomVideoService {
    videoCanvas;

    prevIsSelfVideoOn = false;

    prevIsParticipantVideoOn = false;

    mediaStream;

    localAudio;

    localVideo;

    supportsMultipleVideos = false; // if ShareBufferArrays is supported by the browser

    selfVideoElement: HTMLVideoElement;

    guideUserID: number;

    isUpdatingSelfVideo = false;

    logger: Logger;

    loggerInfo: { name: string; tourID: string };

    constructor() {
        this.localAudio = ZoomVideo.createLocalAudioTrack();
        this.localVideo = ZoomVideo.createLocalVideoTrack();
    }

    setLogger(logger: Logger, loggerInfo: { name: string; tourID: string }) {
        this.logger = logger;
        this.loggerInfo = loggerInfo;
    }

    setMediaStream(mediaStream) {
        this.mediaStream = mediaStream;
        this.supportsMultipleVideos =
            this.mediaStream.isSupportMultipleVideos();
        if (!this.supportsMultipleVideos) {
            console.warn("No support for multiple videos");
            // this.selfVideoElement = document.getElementById(
            //     "js-self-video",
            // ) as HTMLVideoElement;
        }
    }

    setVideoCanvas(videoCanvas) {
        this.videoCanvas = videoCanvas;
    }

    async toggleSelfVideo(
        isVideoOn: boolean,
        userID: number,
        forceRender = false,
    ) {
        if (
            !forceRender &&
            (typeof isVideoOn !== "boolean" ||
                this.prevIsSelfVideoOn === isVideoOn)
        ) {
            return;
        }
        if (this.isUpdatingSelfVideo) {
            return;
        }
        this.logger.debug(
            `${JSON.stringify(
                this.loggerInfo,
            )}: toggleSelfVideo ${isVideoOn} for user ${userID}`,
        );
        this.isUpdatingSelfVideo = true;
        if (!this.supportsMultipleVideos) {
            this.selfVideoElement = document.getElementById(
                "js-self-video",
            ) as HTMLVideoElement;
        }
        if (isVideoOn) {
            if (!this.supportsMultipleVideos) {
                // to render self video for guide on chrome
                // console.log("render self video in", this.selfVideoElement);
                try {
                    await this.localVideo.start(this.selfVideoElement);
                    await this.localAudio.start();
                } catch (error) {
                    this.logger.error(
                        `${JSON.stringify(
                            this.loggerInfo,
                        )}: start local video error ${error}`,
                    );
                }

                if (!forceRender) {
                    this.mediaStream
                        .startVideo({ videoElement: this.selfVideoElement })
                        .catch((error) => {
                            this.logger.error(
                                `${JSON.stringify(
                                    this.loggerInfo,
                                )}: startVideo zoom error ${JSON.stringify(
                                    error,
                                )}`,
                            );
                        });
                }
            } else if (!forceRender) {
                this.mediaStream
                    .startVideo()
                    .then(async (result) => {
                        await this.mediaStream.renderVideo(
                            this.videoCanvas,
                            userID,
                            this.videoCanvas.clientWidth,
                            this.videoCanvas.clientHeight,
                            0,
                            0,
                            VIDEO_QUALITY_360P,
                        );
                    })
                    .catch((error) => {
                        this.logger.error(
                            `${JSON.stringify(
                                this.loggerInfo,
                            )}: start self video error ${JSON.stringify(
                                error,
                            )}`,
                        );
                    });
            }
        } else {
            try {
                if (!this.supportsMultipleVideos) {
                    await this.localVideo.stop(this.selfVideoElement);
                    await this.localAudio.stop();
                }
                console.log("stopped local video");
                await this.mediaStream.stopVideo();
                await this.mediaStream.stopRenderVideo(
                    this.videoCanvas,
                    userID,
                );
                await this.mediaStream.clearVideoCanvas(this.videoCanvas);
            } catch (error) {
                this.logger.error(
                    `${JSON.stringify(
                        this.loggerInfo,
                    )}: stop self video error ${JSON.stringify(error)}`,
                );
            }
        }
        this.prevIsSelfVideoOn = isVideoOn;
        this.isUpdatingSelfVideo = false;
    }

    async toggleParticipantVideo(
        isVideoOn: boolean,
        participantId: number,
        forceRender = false,
    ) {
        if (
            !forceRender &&
            (typeof isVideoOn !== "boolean" ||
                this.prevIsParticipantVideoOn === isVideoOn)
        ) {
            return;
        }
        this.guideUserID = participantId; // because we are rendrering only guide
        if (isVideoOn) {
            this.logger.debug(
                `${JSON.stringify(
                    this.loggerInfo,
                )}: start render video on canvas for guide`,
            );
            try {
                await this.mediaStream.renderVideo(
                    this.videoCanvas,
                    participantId,
                    this.videoCanvas.clientWidth,
                    this.videoCanvas.clientHeight,
                    0,
                    0,
                    VIDEO_QUALITY_360P,
                );
            } catch (error) {
                console.error(error);
                this.logger.error(
                    `${JSON.stringify(
                        this.loggerInfo,
                    )}: renderVideo error ${JSON.stringify(error)} ${error}`,
                );
                return;
            }
        } else if (this.mediaStream) {
            await this.mediaStream.stopRenderVideo(
                this.videoCanvas,
                participantId,
            );
            await this.mediaStream.clearVideoCanvas(this.videoCanvas);
        }
        this.prevIsParticipantVideoOn = isVideoOn;
    }

    async cancelLocalVideo() {
        if (!this.supportsMultipleVideos && !this.isUpdatingSelfVideo) {
            try {
                await this.localVideo.stop(this.selfVideoElement);
                await this.localAudio.stop();
                this.prevIsSelfVideoOn = false;
            } catch (error) {
                this.logger.error(
                    `${JSON.stringify(
                        this.loggerInfo,
                    )}: cancelLocalVideo ${JSON.stringify(error)}`,
                );
            }
        }
    }

    async clearVideoCanvas() {
        if (this.mediaStream) {
            try {
                await this.mediaStream.stopRenderVideo(
                    this.videoCanvas,
                    this.guideUserID,
                );
                await this.mediaStream.clearVideoCanvas(this.videoCanvas);
            } catch (error) {
                this.logger.error(
                    `${JSON.stringify(
                        this.loggerInfo,
                    )}: cancel participant video ${error} ${JSON.stringify(
                        error,
                    )}`,
                );
            }
        }
    }
}
